import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { DashActionSurface } from "@mirage/analytics/events/enums/dash_action_surface";
import type { FileDownloadStatus } from "@mirage/analytics/events/enums/file_download_status";
import { PAP_Close_ResultPreviewModal } from "@mirage/analytics/events/types/close_result_preview_modal";
import { PAP_Interact_ResultPreviewModal } from "@mirage/analytics/events/types/interact_result_preview_modal";
import { PAP_Open_ResultPreviewModal } from "@mirage/analytics/events/types/open_result_preview_modal";
import { useCallback, useEffect, useRef } from "react";

type UsePreviewModalAnalyticsProps = {
  isOpen: boolean;
  searchSessionId?: string;
  connector?: string;
  resultUuid: string;
  dashActionSurface?: DashActionSurface;
  previewModalUuid: string; // Now passed from parent
};

export function usePreviewModalAnalytics({
  isOpen,
  searchSessionId,
  connector,
  resultUuid,
  dashActionSurface,
  previewModalUuid, // Accept from props
}: UsePreviewModalAnalyticsProps) {
  const { reportPapEvent } = useMirageAnalyticsContext();
  // Remove the previewModalUuid ref since it's now passed as prop
  const openTimeRef = useRef<number | null>(null);
  const downloadStartTimeRef = useRef<number | null>(null);
  const fileDownloadStatusRef = useRef<FileDownloadStatus | undefined>(
    undefined,
  );
  const fileDownloadLatencyRef = useRef<number | undefined>(undefined);

  // Track modal open/close
  useEffect(() => {
    if (isOpen) {
      openTimeRef.current = Date.now();
      downloadStartTimeRef.current = Date.now();
      // Reset download tracking for new session
      fileDownloadStatusRef.current = undefined;
      fileDownloadLatencyRef.current = undefined;

      reportPapEvent(
        PAP_Open_ResultPreviewModal({
          previewModalUuid, // Use prop instead of ref
          searchSessionId,
          connector,
          resultUuid,
          dashActionSurface,
        }),
      );
    } else if (openTimeRef.current) {
      reportPapEvent(
        PAP_Close_ResultPreviewModal({
          previewModalUuid, // Use prop instead of ref
          fileDownloadStatus: fileDownloadStatusRef.current,
          fileDownloadLatency: fileDownloadLatencyRef.current,
        }),
      );
      openTimeRef.current = null;
      downloadStartTimeRef.current = null;
      fileDownloadStatusRef.current = undefined;
      fileDownloadLatencyRef.current = undefined;
    }
  }, [
    isOpen,
    reportPapEvent,
    searchSessionId,
    connector,
    resultUuid,
    dashActionSurface,
    previewModalUuid, // Add to dependencies
  ]);

  const logFileDownloadComplete = useCallback((status: FileDownloadStatus) => {
    if (downloadStartTimeRef.current) {
      fileDownloadLatencyRef.current =
        Date.now() - downloadStartTimeRef.current;
    }
    fileDownloadStatusRef.current = status;
  }, []);

  const logZoomInteraction = useCallback(
    (zoomLevel: number, isZoomIn?: boolean) => {
      reportPapEvent(
        PAP_Interact_ResultPreviewModal({
          previewModalUuid, // Use prop instead of ref
          previewModalInteractionType: isZoomIn ? "zoom_in" : "zoom_out",
          zoomPercent: zoomLevel,
        }),
      );
    },
    [reportPapEvent, previewModalUuid],
  );

  const logPanelResize = useCallback(
    (width: number) => {
      reportPapEvent(
        PAP_Interact_ResultPreviewModal({
          previewModalUuid, // Use prop instead of ref
          previewModalInteractionType: "resize_panel",
          sidePanelWidthPercent: width,
        }),
      );
    },
    [reportPapEvent, previewModalUuid],
  );

  const logNavigation = useCallback(
    (isNext: boolean) => {
      reportPapEvent(
        PAP_Interact_ResultPreviewModal({
          previewModalUuid, // Use prop instead of ref
          previewModalInteractionType: isNext
            ? "nav_next_page"
            : "nav_prev_page",
        }),
      );
    },
    [reportPapEvent, previewModalUuid],
  );

  const logOpenInProvider = useCallback(() => {
    reportPapEvent(
      PAP_Interact_ResultPreviewModal({
        previewModalUuid, // Use prop instead of ref
        previewModalInteractionType: "open_file_in_provider",
      }),
    );
  }, [reportPapEvent, previewModalUuid]);

  const logCopyLink = useCallback(() => {
    reportPapEvent(
      PAP_Interact_ResultPreviewModal({
        previewModalUuid, // Use prop instead of ref
        previewModalInteractionType: "copy_link",
      }),
    );
  }, [reportPapEvent, previewModalUuid]);

  const logAddToStack = useCallback(() => {
    reportPapEvent(
      PAP_Interact_ResultPreviewModal({
        previewModalUuid, // Use prop instead of ref
        previewModalInteractionType: "add_to_stack",
      }),
    );
  }, [reportPapEvent, previewModalUuid]);

  const logAskFollowUpQuestion = useCallback(() => {
    reportPapEvent(
      PAP_Interact_ResultPreviewModal({
        previewModalUuid, // Use prop instead of ref
        previewModalInteractionType: "ask_follow_up_question",
      }),
    );
  }, [reportPapEvent, previewModalUuid]);

  return {
    logZoomInteraction,
    logPanelResize,
    logNavigation,
    logOpenInProvider,
    logCopyLink,
    logAddToStack,
    logAskFollowUpQuestion,
    logFileDownloadComplete,
  };
}
